import React from 'react';

import useApi from '@/hooks/useApi';
import useModal from '@/hooks/useModal';
import { useSlider } from '@/hooks/useSlider';

import { ButtonsSlider } from '@/components/global/ButtonSlider';

import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TInteractionCardClick } from '@/services/requests/Interactions/types';
import { getTerms } from '@/services/requests/Liferay';
import { TResponseTerms } from '@/services/requests/Liferay/types';
import { TRecommendation } from '@/services/requests/Recommendation/types/template5';

import { getVisibleCardsIdByElement } from '@/helpers/HtmlHelper/getVisibleCardsIdByElement';
import { isHttpLink } from '@/helpers/StringHelper/isHttpLink';

import { Card } from './Card';

type TProps = {
  data: TRecommendation;
};

export const Slider = ({ data }: TProps) => {
  const { id, cards } = data;

  const modal = useModal();

  const { Api, ApiLiferay } = useApi();
  const useSliderData = useSlider(
    data,
    'tabName',
    true,
    '1',
    'vitrine-template5__card',
  );

  const {
    sliderState,
    moveSlider,
    containerRef,
    checkScrollLimit,
    moveSliderTo,
  } = useSliderData;

  async function handleCardClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    cardId: string,
  ) {
    const redirectURL = event.currentTarget.dataset.link;

    if (redirectURL) {
      const getData = async () => {
        const getTermsData: TResponseTerms | undefined = await getTerms(
          ApiLiferay,
          redirectURL,
        );
        if (getTermsData) {
          modal.setContent(getTermsData.title, getTermsData.content);
        }
      };

      if (!isHttpLink(redirectURL)) {
        event.preventDefault();

        modal.setContent('Carregando...', '');
        modal.setShow(true);
        getData();
      } else {
        await dispatchInteraction<TInteractionCardClick>(Api, 'card-click', {
          recommendationId: id,
          tabName: 'tabName',
          cardId,
          cardPosition: cards.findIndex(item => item.id === cardId) + 1,
          cards: getVisibleCardsIdByElement(
            containerRef.current as HTMLElement,
            'card-click',
          ),
        });
      }
    }
  }

  return (
    <div className="vitrine-template5__slider" data-testid="slider">
      <ButtonsSlider
        handleClick={e => moveSlider(e)}
        sliderScroll={sliderState.scroll}
        checkScrollLimit={checkScrollLimit}
        template="5"
      />
      <div
        className="vitrine-template5__cards"
        ref={containerRef}
        data-testid="cards"
      >
        {cards.map(card => (
          <Card
            key={card.id}
            data={card}
            onClick={e => handleCardClick(e, card.id)}
          />
        ))}
      </div>
      <div className="vitrine-template5__slider-dots 22">
        {cards.map((card, i) => {
          return (
            <span
              key={`dot${card.id}`}
              className={`vitrine-template5__slider-dot${
                i + 1 === sliderState.current ? ' current' : ''
              }`}
              role="button"
              tabIndex={0}
              onClick={() => moveSliderTo(i)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  moveSliderTo(i);
                }
              }}
            >
              {i + 1}
            </span>
          );
        })}
      </div>
    </div>
  );
};
